<template>
  <div class="panel-list-page hp-page" dir="rtl">
    <div class="pl-card">
      <div class="pl-card__header">
        <div class="pl-card__title-wrap">
          <span class="pl-card__icon" aria-hidden="true">
            <CIcon name="navDisplay"/>
          </span>
          <div>
            <h1 class="pl-card__title">چیدمان صفحه اصلی</h1>
            <p class="pl-card__sub">
              <span v-if="dirty" class="hp-status hp-status--warn">تغییرات ذخیره نشده</span>
              <span v-else-if="config.updatedAt" class="hp-status">آخرین ذخیره: {{ formatDate(config.updatedAt) }}</span>
              <span v-else>بخش‌ها را مرتب کنید، ویرایش کنید و ذخیره کنید</span>
            </p>
          </div>
        </div>
        <div class="pl-toolbar">
          <button type="button" class="pl-btn pl-btn--ghost" :disabled="saving || loading" @click="resetDefault">
            بارگذاری پیش‌فرض
          </button>
          <button type="button" class="pl-btn pl-btn--primary" :disabled="saving || !dirty || loading" @click="save">
            {{ saving ? 'در حال ذخیره...' : 'ذخیره تغییرات' }}
          </button>
        </div>
      </div>

      <div class="pl-card__body">
        <p class="hp-hint">
          ترتیب بخش‌ها را با کشیدن دستهٔ کنار هر ردیف عوض کنید. هر بخش را می‌توان برای دسکتاپ، موبایل یا هر دو فعال کرد.
        </p>

        <div class="hp-addbar">
          <label class="hp-field">
            <span class="hp-field__label">نوع بخش جدید</span>
            <select v-model="addType" class="hp-select">
              <option v-for="opt in typeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
          </label>
          <button type="button" class="pl-btn pl-btn--primary" @click="addSection">
            <CIcon name="iconPlus"/>
            <span>افزودن بخش</span>
          </button>
        </div>

        <div v-if="loading" class="hp-skel" aria-busy="true">
          <div v-for="n in 4" :key="'sk' + n" class="hp-skel__row"></div>
        </div>

        <div v-else-if="!config.sections.length" class="pl-empty">
          <span class="pl-empty__icon" aria-hidden="true">
            <CIcon name="navDisplay"/>
          </span>
          <p class="pl-empty__title">هیچ بخشی تعریف نشده</p>
          <p class="pl-empty__sub">از نوار بالا یک نوع بخش انتخاب کنید و اضافه کنید.</p>
        </div>

        <ul v-else class="hp-list">
          <li
            v-for="(sec, si) in config.sections"
            :key="sec.id"
            class="hp-sec"
            :class="{
              'is-open': open[sec.id],
              'is-disabled': !sec.enabled,
              'is-drop': dropIndex === si
            }"
            @dragover.prevent="dropIndex = si"
            @dragleave="dropIndex = dropIndex === si ? -1 : dropIndex"
            @drop.prevent="onDrop(si)"
          >
            <div class="hp-sec__bar">
              <button
                type="button"
                class="hp-handle"
                draggable="true"
                title="جابجایی"
                @dragstart="onDragStart(si, $event)"
                @dragend="dragIndex = -1; dropIndex = -1"
              >⋮⋮</button>

              <span class="hp-sec__index">{{ si + 1 }}</span>

              <button type="button" class="hp-sec__main" @click="toggleOpen(sec.id)">
                <span class="hp-type">{{ typeLabel(sec.type) }}</span>
                <span class="hp-sec__title">{{ sec.title || 'بدون عنوان' }}</span>
              </button>

              <div class="hp-sec__ops">
                <select v-model="sec.device" class="hp-select hp-select--sm" title="نمایش در دستگاه">
                  <option value="all">همه</option>
                  <option value="desktop">دسکتاپ</option>
                  <option value="mobile">موبایل</option>
                </select>

                <label class="hp-switch" :title="sec.enabled ? 'فعال' : 'غیرفعال'">
                  <input type="checkbox" v-model="sec.enabled">
                  <span class="hp-switch__ui"></span>
                </label>

                <button type="button" class="hp-icon hp-icon--blue" title="کپی" @click="duplicate(si)">
                  <CIcon name="iconClone"/>
                </button>
                <button type="button" class="hp-icon hp-icon--red" title="حذف" @click="askDelete(si)">
                  <CIcon name="iconTrash"/>
                </button>
                <button type="button" class="hp-icon hp-icon--slate" :title="open[sec.id] ? 'بستن' : 'ویرایش'" @click="toggleOpen(sec.id)">
                  {{ open[sec.id] ? '−' : '+' }}
                </button>
              </div>
            </div>

            <div v-if="open[sec.id]" class="hp-sec__body">
              <div class="hp-grid">
                <label class="hp-field hp-field--grow">
                  <span class="hp-field__label">عنوان بخش</span>
                  <input v-model="sec.title" class="hp-input" type="text" placeholder="عنوان نمایشی">
                </label>

                <label v-if="sec.type === 'banner_grid'" class="hp-field">
                  <span class="hp-field__label">تعداد ستون</span>
                  <select v-model.number="sec.settings.columns" class="hp-select">
                    <option :value="1">۱ ستون</option>
                    <option :value="2">۲ ستون</option>
                    <option :value="4">۴ ستون</option>
                  </select>
                </label>

                <label v-if="sec.type === 'hero_slider'" class="hp-field">
                  <span class="hp-field__label">فاصله اسلایدها (میلی‌ثانیه)</span>
                  <input v-model.number="sec.settings.interval" class="hp-input" type="number" min="1000" step="500">
                </label>
              </div>

              <!-- product based -->
              <template v-if="sec.type === 'product_carousel' || sec.type === 'amazing_offers'">
                <div class="hp-block">
                  <h3 class="hp-block__title">منبع محصولات</h3>
                  <div class="hp-grid">
                    <label class="hp-field">
                      <span class="hp-field__label">منبع</span>
                      <select v-model="sec.settings.source" class="hp-select">
                        <option
                          v-for="opt in (sec.type === 'amazing_offers' ? offerSources : sourceOptions)"
                          :key="opt.value"
                          :value="opt.value"
                        >{{ opt.label }}</option>
                      </select>
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">تعداد</span>
                      <input v-model.number="sec.settings.limit" class="hp-input" type="number" min="1" max="40">
                    </label>
                    <label class="hp-field hp-field--grow">
                      <span class="hp-field__label">لینک «مشاهده همه»</span>
                      <input v-model="sec.settings.viewAllLink" class="hp-input" type="text" placeholder="/shop/cat/...">
                    </label>
                    <label v-if="sec.type === 'product_carousel' && sec.settings.source === 'category'" class="hp-field">
                      <span class="hp-field__label">دسته‌بندی</span>
                      <select v-model="sec.settings.categoryId" class="hp-select">
                        <option v-for="c in categoryOptions" :key="'c' + c.value" :value="c.value">{{ c.label }}</option>
                      </select>
                    </label>
                    <label v-if="sec.type === 'product_carousel' && sec.settings.source === 'top_category'" class="hp-field">
                      <span class="hp-field__label">شماره دسته‌بندی اصلی</span>
                      <input v-model.number="sec.settings.categoryIndex" class="hp-input" type="number" min="1" max="30">
                      <span class="hp-field__hint">عنوان و «مشاهده همه» در صورت خالی بودن از همین دسته پر می‌شود</span>
                    </label>
                  </div>

                  <div v-if="sec.type === 'amazing_offers'" class="hp-grid">
                    <label class="hp-field">
                      <span class="hp-field__label">پایان شمارش معکوس</span>
                      <date-picker
                        :value="toPicker(sec.settings.endsAt)"
                        type="datetime"
                        format="YYYY-MM-DD HH:mm"
                        display-format="jYYYY/jMM/jDD HH:mm"
                        :clearable="true"
                        @input="sec.settings.endsAt = fromPicker($event)"
                      />
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">رنگ پس‌زمینه</span>
                      <div class="hp-color">
                        <input v-model="sec.settings.bgColor" type="color" class="hp-color__swatch">
                        <input v-model="sec.settings.bgColor" class="hp-input" type="text" placeholder="#ef394e">
                      </div>
                    </label>
                  </div>

                  <HpProductPicker v-if="sec.settings.source === 'manual'" v-model="sec.settings.productIds" class="mt-2"/>
                </div>
              </template>

              <template v-if="sec.type === 'html_block'">
                <div class="hp-block">
                  <h3 class="hp-block__title">کد HTML</h3>
                  <textarea v-model="sec.settings.html" class="hp-textarea" rows="8" dir="ltr" placeholder="<div>...</div>"></textarea>
                  <p class="hp-field__hint">اسکریپت و iframe برای امنیت حذف می‌شوند.</p>
                </div>
              </template>

              <div v-if="sec.type === 'category_icons' || sec.type === 'brand_strip'" class="hp-block">
                <h3 class="hp-block__title">نمایش خودکار</h3>
                <div class="hp-grid">
                  <label class="hp-check hp-field--grow">
                    <input type="checkbox" v-model="sec.settings.auto">
                    <span>
                      {{ sec.type === 'category_icons'
                        ? 'اگر آیتمی تعریف نشده، دسته‌بندی‌های اصلی سایت خودکار نمایش داده شوند'
                        : 'اگر آیتمی تعریف نشده، برندهای دارای لوگو خودکار نمایش داده شوند' }}
                    </span>
                  </label>
                  <label class="hp-field">
                    <span class="hp-field__label">حداکثر تعداد</span>
                    <input v-model.number="sec.settings.limit" class="hp-input" type="number" min="1" max="40">
                  </label>
                </div>
              </div>

              <div v-if="sec.type === 'shipping_info'" class="hp-block">
                <label class="hp-field">
                  <span class="hp-field__label">رنگ پس‌زمینه (اختیاری)</span>
                  <input v-model="sec.settings.bgColor" class="hp-input" type="text" placeholder="#f5f5f5">
                </label>
              </div>

              <!-- items -->
              <div v-if="hasItems(sec.type)" class="hp-block">
                <div class="hp-block__head">
                  <h3 class="hp-block__title">آیتم‌ها ({{ sec.items.length }})</h3>
                  <button type="button" class="pl-btn pl-btn--sm pl-btn--ghost" @click="addItem(sec)">
                    <CIcon name="iconPlus"/>
                    <span>افزودن آیتم</span>
                  </button>
                </div>

                <div v-if="!sec.items.length" class="hp-items-empty">آیتمی اضافه نشده است.</div>

                <div v-for="(it, ii) in sec.items" :key="sec.id + '_' + ii" class="hp-item">
                  <div class="hp-item__bar">
                    <span class="hp-item__num">آیتم {{ ii + 1 }}</span>
                    <div class="hp-item__ops">
                      <button type="button" class="pl-icon-btn" :disabled="ii === 0" @click="moveItem(sec, ii, -1)">▲</button>
                      <button type="button" class="pl-icon-btn" :disabled="ii === sec.items.length - 1" @click="moveItem(sec, ii, 1)">▼</button>
                      <button type="button" class="pl-icon-btn pl-icon-btn--danger" @click="removeItem(sec, ii)">✕</button>
                    </div>
                  </div>

                  <div v-if="sec.type === 'hero_slider' || sec.type === 'banner_grid'" class="hp-grid">
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.image" label="تصویر دسکتاپ"/></div>
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.mobileImage" label="تصویر موبایل (اختیاری)"/></div>
                    <label class="hp-field hp-field--grow">
                      <span class="hp-field__label">لینک</span>
                      <input v-model="it.link" class="hp-input" type="text">
                    </label>
                    <label class="hp-field hp-field--grow">
                      <span class="hp-field__label">متن جایگزین (alt)</span>
                      <input v-model="it.alt" class="hp-input" type="text">
                    </label>
                  </div>

                  <div v-else-if="sec.type === 'category_icons'" class="hp-grid">
                    <label class="hp-field">
                      <span class="hp-field__label">دسته‌بندی</span>
                      <select v-model="it.categoryId" class="hp-select">
                        <option v-for="c in categoryOptions" :key="'ic' + c.value" :value="c.value">{{ c.label }}</option>
                      </select>
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">عنوان (خالی = نام دسته)</span>
                      <input v-model="it.title" class="hp-input" type="text">
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">لینک (خالی = صفحه دسته)</span>
                      <input v-model="it.link" class="hp-input" type="text">
                    </label>
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.icon" label="آیکون"/></div>
                  </div>

                  <div v-else-if="sec.type === 'brand_strip'" class="hp-grid">
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.logo" label="لوگو"/></div>
                    <label class="hp-field">
                      <span class="hp-field__label">نام برند</span>
                      <input v-model="it.title" class="hp-input" type="text">
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">لینک</span>
                      <input v-model="it.link" class="hp-input" type="text">
                    </label>
                  </div>

                  <div v-else-if="sec.type === 'story_circles'" class="hp-grid">
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.image" label="تصویر"/></div>
                    <label class="hp-field">
                      <span class="hp-field__label">عنوان</span>
                      <input v-model="it.title" class="hp-input" type="text">
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">لینک</span>
                      <input v-model="it.link" class="hp-input" type="text">
                    </label>
                  </div>

                  <div v-else-if="sec.type === 'shipping_info'" class="hp-grid">
                    <label class="hp-field">
                      <span class="hp-field__label">آیکن</span>
                      <select v-model="it.icon" class="hp-select">
                        <option v-for="ic in shippingIcons" :key="ic.value" :value="ic.value">{{ ic.label }}</option>
                      </select>
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">عنوان</span>
                      <input v-model="it.title" class="hp-input" type="text">
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">توضیح کوتاه</span>
                      <input v-model="it.subtitle" class="hp-input" type="text">
                    </label>
                    <label class="hp-field">
                      <span class="hp-field__label">لینک (اختیاری)</span>
                      <input v-model="it.link" class="hp-input" type="text">
                    </label>
                    <div class="hp-field hp-field--grow"><HpImageUpload v-model="it.image" label="آیکن سفارشی (اختیاری)"/></div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <CModal title="حذف بخش" color="danger" :show.sync="deleteModal">
      آیا از حذف بخش «{{ deleteTarget >= 0 && config.sections[deleteTarget] ? config.sections[deleteTarget].title : '' }}» مطمئن هستید؟
      <template #footer>
        <button type="button" class="pl-btn pl-btn--ghost" @click="deleteModal = false">انصراف</button>
        <button type="button" class="pl-btn pl-btn--danger" @click="confirmDelete">حذف</button>
      </template>
    </CModal>
  </div>
</template>

<script>
import HpImageUpload from './homepage/HpImageUpload'
import HpProductPicker from './homepage/HpProductPicker'
import {
  getHomepage, getDefaultHomepage, saveHomepage, getCategoryTree,
  SECTION_TYPES, DEVICES, PRODUCT_SOURCES, SHIPPING_ICONS, typeLabel, newSection, newItem
} from './homepage/hpApi'

const ITEM_TYPES = ['hero_slider', 'banner_grid', 'category_icons', 'brand_strip', 'story_circles', 'shipping_info']
const pad = (n) => (n < 10 ? '0' : '') + n

export default {
  name: 'HomepageBuilder',
  components: { HpImageUpload, HpProductPicker },
  data() {
    return {
      loading: true,
      saving: false,
      config: { sections: [], updatedAt: null },
      snapshot: '',
      open: {},
      addType: 'product_carousel',
      typeOptions: SECTION_TYPES,
      deviceOptions: DEVICES,
      sourceOptions: PRODUCT_SOURCES,
      shippingIcons: SHIPPING_ICONS,
      offerSources: PRODUCT_SOURCES.filter((s) => s.value === 'discounted' || s.value === 'manual'),
      categoryOptions: [{ value: null, label: '— انتخاب دسته —' }],
      dragIndex: -1,
      dropIndex: -1,
      deleteModal: false,
      deleteTarget: -1
    }
  },
  computed: {
    dirty() {
      return !this.loading && JSON.stringify(this.config.sections) !== this.snapshot
    }
  },
  created() {
    this.load()
    this.loadCategories()
  },
  mounted() {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (this.dirty && !window.confirm('تغییرات ذخیره نشده‌اند. از این صفحه خارج می‌شوید؟')) return next(false)
    return next()
  },
  methods: {
    typeLabel,
    hasItems(t) {
      return ITEM_TYPES.indexOf(t) !== -1
    },
    notify(message, type) {
      if (this.$toast) this.$toast.open({ message: message, type: type || 'success' })
      else alert(message)
    },
    onBeforeUnload(e) {
      if (!this.dirty) return undefined
      e.preventDefault()
      e.returnValue = ''
      return ''
    },
    normalize(cfg) {
      const sections = ((cfg && cfg.sections) || []).map((s) => {
        const base = newSection(s.type)
        return Object.assign(base, s, { settings: Object.assign({}, base.settings, s.settings || {}), items: (s.items || []).map((i) => Object.assign(newItem(s.type), i)) })
      })
      return { sections: sections, updatedAt: (cfg && cfg.updatedAt) || null }
    },
    setConfig(cfg, markClean) {
      this.config = this.normalize(cfg)
      if (markClean) this.snapshot = JSON.stringify(this.config.sections)
    },
    load() {
      this.loading = true
      getHomepage().then((res) => {
        if (res.data && res.data.error === 0) this.setConfig(res.data.config, true)
        else this.notify((res.data && res.data.msg) || 'خطا در دریافت تنظیمات', 'error')
      }).catch(() => this.notify('خطا در ارتباط با سرور', 'error')).finally(() => { this.loading = false })
    },
    loadCategories() {
      getCategoryTree().then((res) => {
        const out = [{ value: null, label: '— انتخاب دسته —' }]
        const walk = (nodes, depth) => (nodes || []).forEach((n) => {
          out.push({ value: n.id, label: '— '.repeat(depth) + (n.name || n.title || n.label || ('#' + n.id)) })
          walk(n.children, depth + 1)
        })
        walk((res.data && (res.data.data || res.data.orders)) || [], 0)
        this.categoryOptions = out
      }).catch(() => {})
    },
    resetDefault() {
      if (!window.confirm('تنظیمات پیش‌فرض جایگزین بخش‌های فعلی شود؟ (تا قبل از ذخیره قابل برگشت است)')) return
      getDefaultHomepage().then((res) => {
        if (res.data && res.data.config) this.config.sections = this.normalize(res.data.config).sections
      })
    },
    save() {
      this.saving = true
      const payload = { sections: this.config.sections.map((s, i) => Object.assign({}, s, { order: i })) }
      saveHomepage(payload).then((res) => {
        if (res.data && res.data.error === 0) {
          this.setConfig(res.data.config, true)
          this.notify(res.data.msg || 'ذخیره شد')
        } else this.notify((res.data && res.data.msg) || 'خطا در ذخیره', 'error')
      }).catch((err) => {
        this.notify((err.response && err.response.data && err.response.data.msg) || 'خطا در ذخیره', 'error')
      }).finally(() => { this.saving = false })
    },
    toggleOpen(id) {
      this.$set(this.open, id, !this.open[id])
    },
    addSection() {
      const s = newSection(this.addType)
      this.config.sections.push(s)
      this.$set(this.open, s.id, true)
    },
    duplicate(i) {
      const copy = JSON.parse(JSON.stringify(this.config.sections[i]))
      copy.id = 's_' + Math.random().toString(16).slice(2, 12)
      copy.title = (copy.title || '') + ' (کپی)'
      this.config.sections.splice(i + 1, 0, copy)
    },
    askDelete(i) {
      this.deleteTarget = i
      this.deleteModal = true
    },
    confirmDelete() {
      if (this.deleteTarget >= 0) this.config.sections.splice(this.deleteTarget, 1)
      this.deleteTarget = -1
      this.deleteModal = false
    },
    moveSection(i, d) {
      const l = this.config.sections
      const t = l.splice(i, 1)[0]
      l.splice(i + d, 0, t)
    },
    onDragStart(i, e) {
      this.dragIndex = i
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(i))
      }
    },
    onDrop(i) {
      const from = this.dragIndex
      this.dropIndex = -1
      this.dragIndex = -1
      if (from < 0 || from === i) return
      const l = this.config.sections
      const t = l.splice(from, 1)[0]
      l.splice(i, 0, t)
    },
    addItem(sec) {
      sec.items.push(newItem(sec.type))
    },
    removeItem(sec, i) {
      sec.items.splice(i, 1)
    },
    moveItem(sec, i, d) {
      const t = sec.items.splice(i, 1)[0]
      sec.items.splice(i + d, 0, t)
    },
    toPicker(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    fromPicker(v) {
      if (!v) return ''
      const d = new Date(String(v).replace(' ', 'T'))
      return isNaN(d.getTime()) ? '' : d.toISOString()
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('fa-IR')
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
.hp-page {
  max-width: none;
}

.hp-status {
  color: var(--panel-muted, #6b7280);
}

.hp-status--warn {
  color: #b45309;
  font-weight: 700;
}

.hp-hint {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.7;
  color: var(--panel-muted, #6b7280);
}

.hp-addbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 12px;
  background: var(--panel-surface-2, #f8fafc);
}

.hp-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hp-sec {
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 12px;
  background: var(--panel-surface, #fff);
  box-shadow: none;
  overflow: hidden;
}

.hp-sec.is-disabled {
  opacity: .72;
}

.hp-sec.is-drop {
  border-color: #111111;
  background: #fafafa;
}

.hp-sec.is-open {
  border-color: rgba(17, 17, 17, .28);
}

.hp-sec__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  min-height: 52px;
}

.hp-handle {
  border: 0;
  background: transparent;
  color: var(--panel-muted, #9ca3af);
  cursor: grab;
  font-size: 14px;
  letter-spacing: -2px;
  padding: 4px;
  line-height: 1;
  user-select: none;
}

.hp-sec__index {
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: var(--panel-surface-2, #f1f5f9);
  color: var(--panel-muted, #6b7280);
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hp-type {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  background: #111111;
  color: #fff;
}

.hp-sec__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: right;
}

.hp-sec__title {
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--panel-text, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hp-badge {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.hp-badge--on {
  background: rgba(22, 163, 74, .12);
  color: #15803d;
}

.hp-badge--off {
  background: var(--panel-surface-2, #f1f5f9);
  color: #6b7280;
}

.hp-sec__ops {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
  flex-shrink: 0;
}

.hp-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
}

.hp-icon .c-icon {
  width: 18px;
  height: 18px;
}

.hp-icon--blue {
  background: rgba(37, 99, 235, .14);
  color: #1d4ed8;
}

.hp-icon--blue:hover {
  background: rgba(37, 99, 235, .22);
}

.hp-icon--red {
  background: rgba(220, 38, 38, .14);
  color: #dc2626;
}

.hp-icon--red:hover {
  background: rgba(220, 38, 38, .22);
}

.hp-icon--slate {
  background: rgba(100, 116, 139, .14);
  color: #334155;
}

.hp-icon--slate:hover {
  background: rgba(100, 116, 139, .22);
}

.hp-sec__body {
  padding: 14px 14px 16px;
  border-top: 1px solid var(--panel-border, #e5e7eb);
  background: #fcfcfd;
}

.hp-block {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 12px;
  background: var(--panel-surface, #fff);
}

.hp-block__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.hp-block__title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 800;
  color: var(--panel-text, #111827);
}

.hp-block__head .hp-block__title {
  margin: 0;
}

.hp-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.hp-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
  flex: 1 1 160px;
}

.hp-field--grow {
  flex: 1 1 260px;
}

.hp-field__label {
  font-size: 12px;
  font-weight: 700;
  color: var(--panel-muted, #6b7280);
}

.hp-field__hint {
  margin: 4px 0 0;
  font-size: 11px;
  color: var(--panel-muted, #9ca3af);
  line-height: 1.5;
}

.hp-input,
.hp-select,
.hp-textarea {
  width: 100%;
  min-height: 38px;
  padding: 8px 10px;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 10px;
  background: var(--panel-surface, #fff);
  color: var(--panel-text, #111827);
  font-family: inherit;
  font-size: 13px;
  outline: none;
  box-shadow: none;
}

.hp-select--sm {
  width: 108px;
  min-width: 108px;
  max-width: 108px;
  flex: 0 0 108px;
  min-height: 36px;
  padding: 4px 8px;
  font-size: 13px;
  border-radius: 10px;
}

.hp-input:focus,
.hp-select:focus,
.hp-textarea:focus {
  border-color: #111111;
}

.hp-textarea {
  min-height: 160px;
  resize: vertical;
  line-height: 1.6;
}

.hp-color {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hp-color__swatch {
  width: 38px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
}

.hp-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--panel-text, #111827);
  cursor: pointer;
}

.hp-check input {
  margin-top: 4px;
}

.hp-switch {
  position: relative;
  width: 40px;
  height: 22px;
  display: inline-block;
  cursor: pointer;
  flex-shrink: 0;
}

.hp-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.hp-switch__ui {
  position: absolute;
  inset: 0;
  background: #d1d5db;
  border-radius: 99px;
  transition: background .15s ease;
}

.hp-switch__ui::after {
  content: '';
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  transition: transform .15s ease;
}

.hp-switch input:checked + .hp-switch__ui {
  background: #16a34a;
}

.hp-switch input:checked + .hp-switch__ui::after {
  transform: translateX(-18px);
}

.hp-item {
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  background: var(--panel-surface-2, #f8fafc);
}

.hp-item:last-child {
  margin-bottom: 0;
}

.hp-item__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.hp-item__num {
  font-size: 12px;
  font-weight: 700;
  color: var(--panel-muted, #6b7280);
}

.hp-item__ops {
  display: flex;
  gap: 4px;
}

.hp-items-empty {
  font-size: 13px;
  color: var(--panel-muted, #6b7280);
  padding: 8px 0;
}

.hp-skel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hp-skel__row {
  height: 56px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9 25%, #e5e7eb 37%, #f1f5f9 63%);
  background-size: 400% 100%;
  animation: hp-shimmer 1.2s ease infinite;
}

@keyframes hp-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

.pl-btn--danger {
  background: #dc2626 !important;
  color: #fff !important;
  border-color: #dc2626 !important;
}

@media (max-width: 720px) {
  .hp-sec__bar {
    flex-wrap: wrap;
  }
  .hp-sec__main {
    flex: 1 1 calc(100% - 80px);
  }
  .hp-sec__ops {
    width: 100%;
    justify-content: flex-start;
  }
  .hp-type {
    display: none;
  }
}
</style>
